import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Copy, CheckCircle2, XCircle, Clock, ExternalLink, Users, Building2, Briefcase, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ConfirmCloseCampaignDialog } from "@/components/ConfirmCloseCampaignDialog";
import { CampaignStatsCharts } from "@/components/CampaignStatsCharts";
import { ParticipationMonitoring } from "@/components/ParticipationMonitoring";
import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";
import SalviaLayout from "@/components/SalviaLayout";

export default function QuestionarioDetalhe() {
  usePageTitle("Detalhes do Questionário");
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const campaignId = parseInt(id || "0");
  const isStructuralChannel = user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_consultant";
  // Canal e client_rh ficam em modo leitura; apenas administradores internos gerenciam campanhas.
  const canManageCampaigns = user?.role === "admin" || user?.role === "salvia_operation_admin";
  // V100: client_rh tem acesso somente leitura (sem criar/encerrar)
  const isClientRh = user?.role === "client_rh";
  const backRoute = isClientRh ? "/meus-questionarios" : "/questionarios";
  const backLabel = isClientRh ? "Voltar para Questionários" : "Voltar para Questionários";

  // Estado do modal de confirmação de encerramento
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);

  // Query principal: buscar campanha por ID
  const { 
    data: campaign, 
    isLoading: loadingCampaign,
    error: campaignError 
  } = trpc.questionnaire.campaign.getById.useQuery(
    { campaignId },
    { enabled: !!campaignId && campaignId > 0 }
  );

  const { data: progress, isLoading: loadingProgress } = trpc.questionnaire.monitoring.getProgress.useQuery(
    { campaignId },
    { enabled: !!campaign, refetchInterval: 5000 } // Atualizar a cada 5s
  );

  // Query de resultados parciais mantida comentada para uso futuro (removida da UI em V78)
  // const { data: partialResults, isLoading: loadingPartialResults } = trpc.questionnaire.monitoring.getPartialResults.useQuery(
  //   { campaignId, scope: "COMPANY", scopeId: campaign?.companyId },
  //   { 
  //     enabled: campaign?.status === "ACTIVE" && !!campaign?.templateId && !!campaign?.templateVersion 
  //   }
  // );

  const { data: finalSnapshot, isLoading: loadingFinalSnapshot } = trpc.questionnaire.monitoring.getFinalSnapshot.useQuery(
    { campaignId },
    { enabled: campaign?.status === "CLOSED" }
  );

  // Mutations
  const activateMutation = trpc.questionnaire.campaign.activate.useMutation({
    onSuccess: () => {
      toast.success("Campanha ativada com sucesso!");
      window.location.reload();
    },
    onError: (error) => {
      toast.error(`Erro ao ativar campanha: ${error.message}`);
    },
  });

  const closeMutation = trpc.questionnaire.campaign.closeAndConsolidate.useMutation({
    onSuccess: () => {
      toast.success("Campanha encerrada e resultados consolidados!");
      window.location.reload();
    },
    onError: (error) => {
      toast.error(`Erro ao encerrar campanha: ${error.message}`);
    },
  });

  // Handlers
  const handleActivate = () => {
    if (window.confirm("Tem certeza que deseja ativar esta campanha? Após ativação, o template será congelado e não poderá ser alterado.")) {
      activateMutation.mutate({ campaignId });
    }
  };

  const handleClose = () => {
    setConfirmCloseDialogOpen(true);
  };

  const handleConfirmClose = async () => {
    return new Promise<void>((resolve, reject) => {
      closeMutation.mutate(
        { campaignId },
        {
          onSuccess: () => {
            setConfirmCloseDialogOpen(false);
            resolve();
          },
          onError: (error) => {
            setConfirmCloseDialogOpen(false);
            reject(error);
          },
        }
      );
    });
  };

  const handleCopyLink = () => {
    if (!campaign?.publicToken) return;
    const publicUrl = `${window.location.origin}/q/${campaign.publicToken}`;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copiado para a área de transferência!");
  };

  const handleViewPublic = () => {
    if (!campaign?.publicToken) return;
    window.open(`/q/${campaign.publicToken}`, "_blank");
  };

  // V82b: Consultor Canal não tem acesso ao módulo de Questionários
  // client_rh tem acesso somente leitura — não bloquear
  if (user?.role === "channel_consultant") {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil não possui acesso ao módulo de Questionários.</p>
            <Button variant="outline" onClick={() => setLocation("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }

  // UX de Erros
  if (loadingCampaign) {
    const loadingContent = (
      <div className="container mx-auto py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backRoute)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Questionários
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Carregando...</CardTitle>
            <CardDescription>Aguarde enquanto carregamos os detalhes da campanha.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
    if (isClientRh) return <SalviaLayout>{loadingContent}</SalviaLayout>;
    return loadingContent;
  }

  if (campaignError) {
    const errorCode = (campaignError as any)?.data?.code;
    const errorMessage = campaignError.message;

    let title = "Erro ao carregar campanha";
    let description = "Erro técnico ao carregar a campanha. Tente novamente.";
    let icon = <AlertTriangle className="h-8 w-8 text-yellow-500" />;

    if (errorCode === "NOT_FOUND") {
      title = "Campanha não encontrada";
      description = "A campanha solicitada não existe ou foi removida.";
      icon = <XCircle className="h-8 w-8 text-red-500" />;
    } else if (errorCode === "FORBIDDEN" || errorCode === "UNAUTHORIZED") {
      title = "Acesso negado";
      description = "Você não tem permissão para acessar esta campanha.";
      icon = <XCircle className="h-8 w-8 text-red-500" />;
    }

    const errorContent = (
      <div className="container mx-auto py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backRoute)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Questionários
          </Button>
        </div>
        <Alert variant="destructive">
          <div className="flex items-center gap-4">
            {icon}
            <div>
              <h3 className="font-semibold text-lg">{title}</h3>
              <AlertDescription>{description}</AlertDescription>
              {errorCode !== "NOT_FOUND" && errorCode !== "FORBIDDEN" && (
                <p className="text-sm mt-2 text-muted-foreground">Detalhes técnicos: {errorMessage}</p>
              )}
            </div>
          </div>
        </Alert>
      </div>
    );
    if (isClientRh) return <SalviaLayout>{errorContent}</SalviaLayout>;
    return errorContent;
  }
  if (!campaign) {
    const notFoundContent = (
      <div className="container mx-auto py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backRoute)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Questionários
          </Button>
        </div>
        <Alert>
          <AlertDescription>Campanha não encontrada.</AlertDescription>
        </Alert>
      </div>
    );
    if (isClientRh) return <SalviaLayout>{notFoundContent}</SalviaLayout>;
    return notFoundContent;
  }

  const statusConfig = {
    DRAFT: { label: "Rascunho", variant: "secondary" as const, icon: Clock },
    ACTIVE: { label: "Ativa", variant: "default" as const, icon: CheckCircle2 },
    CLOSED: { label: "Encerrada", variant: "outline" as const, icon: XCircle },
  };

  const status = statusConfig[campaign.status] || statusConfig.DRAFT;
  const StatusIcon = status.icon;

  const content = (
    <div className="container mx-auto py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backRoute)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Questionários
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {canManageCampaigns && campaign.status === "DRAFT" && (
            <Button onClick={handleActivate} disabled={activateMutation.isPending}>
              {activateMutation.isPending ? "Ativando..." : "Ativar Campanha"}
            </Button>
          )}
          {campaign.status === "ACTIVE" && (
            <>
              <Button variant="outline" onClick={handleCopyLink}>
                <Copy className="h-4 w-4 mr-2" />
                Copiar Link
              </Button>
              <Button variant="outline" onClick={handleViewPublic}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Público
              </Button>
              {canManageCampaigns && !isClientRh && (
                <Button variant="destructive" onClick={handleClose} disabled={closeMutation.isPending}>
                  {closeMutation.isPending ? "Encerrando..." : "Encerrar Campanha"}
                </Button>
              )}
            </>
          )}
          {campaign.status === "CLOSED" && (
            <Button variant="outline" onClick={() => setLocation(`/questionarios/${campaignId}/oficial`)}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Ver Documento Oficial
            </Button>
          )}
        </div>
      </div>

      {/* Informações da Campanha */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Campanha #{campaign.id}</CardTitle>
              <CardDescription>
                Criada em {new Date(campaign.createdAt).toLocaleDateString("pt-BR")}
              </CardDescription>
            </div>
            <Badge variant={status.variant} className="flex items-center gap-1">
              <StatusIcon className="h-4 w-4" />
              {status.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Canal</p>
                <p className="text-sm text-muted-foreground">{campaign.channelName || "N/A"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Cliente</p>
                <p className="text-sm text-muted-foreground">
                  {campaign.clientName} ({campaign.clientCnpj})
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium">Template</p>
              <p className="text-sm text-muted-foreground">
                {campaign.templateId ? `ID ${campaign.templateId} ${campaign.templateVersion}` : "Não definido"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">kMin (Anonimato)</p>
              <p className="text-sm text-muted-foreground">{campaign.kMin}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Data de Encerramento</p>
              <p className="text-sm text-muted-foreground">
                {new Date(campaign.endsAt).toLocaleDateString("pt-BR")}
              </p>
            </div>
            {campaign.activatedAt && (
              <div>
                <p className="text-sm font-medium">Ativada em</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(campaign.activatedAt).toLocaleDateString("pt-BR")}
                </p>
              </div>
            )}
            {campaign.closedAt && (
              <div>
                <p className="text-sm font-medium">Encerrada em</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(campaign.closedAt).toLocaleDateString("pt-BR")}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progresso */}
      {campaign.status !== "DRAFT" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Progresso de Respostas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingProgress ? (
              <p className="text-sm text-muted-foreground">Carregando progresso...</p>
            ) : progress ? (
              <div className="space-y-6">
                {/* Gráficos de Respostas */}
                <CampaignStatsCharts
                  bySector={progress.bySector.map((item: any) => ({ sector: item.sector, total: item.count }))}
                  byJobTitle={progress.byJobTitle.map((item: any) => ({ jobTitle: item.jobTitle, total: item.count }))}
                  bySectorAndJob={progress.bySectorAndJob.map((item: any) => ({ sector: item.sector, jobTitle: item.jobTitle, total: item.count, vidas: item.vidas || 0 }))}
                  totalResponses={progress.totalCompleted}
                  totalInProgress={progress.totalInProgress}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma resposta ainda.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* V26+V86: Monitoramento de Participação da Campanha */}
      {campaign.status !== "DRAFT" && (
        <ParticipationMonitoring
          campaignId={campaignId}
          campaignStatus={campaign.status}
          campaignMeta={{
            channelName: campaign.channelName,
            clientName: campaign.clientName,
            clientCnpj: campaign.clientCnpj,
            templateId: campaign.templateId,
            templateVersion: campaign.templateVersion,
            kMin: campaign.kMin,
          }}
        />
      )}


      {/* Snapshot Final (CLOSED) */}
      {campaign.status === "CLOSED" && finalSnapshot && (
        <Card>
          <CardHeader>
            <CardTitle>Resultados Finais (OFICIAL)</CardTitle>
            <CardDescription>
              Estes resultados são imutáveis e oficiais. Gerados em{" "}
              {new Date(campaign.closedAt!).toLocaleDateString("pt-BR")}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded overflow-auto">
              {JSON.stringify(finalSnapshot, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Modal de Confirmação de Encerramento */}
      <ConfirmCloseCampaignDialog
        open={confirmCloseDialogOpen}
        onOpenChange={setConfirmCloseDialogOpen}
        onConfirm={handleConfirmClose}
        isLoading={closeMutation.isPending}
      />
    </div>
  );

  // V100: client_rh usa SalviaLayout para ter sidebar; outros perfis usam layout standalone
  if (isClientRh) {
    return <SalviaLayout>{content}</SalviaLayout>;
  }
  return content;
}
